import { useEffect, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { useTranslation } from "@/i18n";
import { useApiActions } from "@/lib/actions";
import ProviderOption from "./ProviderOption";
import { ProviderConfig } from "./types";

interface ProviderSelectorProps {
  value: string;
  onChange: (providerId: string) => void;
}

const PROVIDER_LABEL_KEYS: Record<string, string> = {
  "anthropic": "providerLabel.anthropic",
  "gemini": "providerLabel.gemini",
  "azure-gpt5mini": "providerLabel.azure-gpt5mini",
};

const PROVIDER_DESC_KEYS: Record<string, string> = {
  "anthropic": "providerDesc.anthropic",
  "gemini": "providerDesc.gemini",
  "azure-gpt5mini": "providerDesc.azure-gpt5mini",
};

export default function ProviderSelector({ value, onChange }: ProviderSelectorProps) {
  const { t } = useTranslation();
  const { getProviders } = useApiActions();
  const [providers, setProviders] = useState<ProviderConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const getProviderLabel = (id: string) => {
    const key = PROVIDER_LABEL_KEYS[id];
    if (key) return t(`documentUpload.selection.${key}`);
    // Fallback to backend-provided name
    const provider = providers.find(p => p.id === id);
    return provider?.name ?? id;
  };

  const getProviderDescription = (id: string) => {
    const key = PROVIDER_DESC_KEYS[id];
    if (key) return t(`documentUpload.selection.${key}`);
    const provider = providers.find(p => p.id === id);
    return provider?.description ?? "";
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await getProviders();
        if (!cancelled) {
          setProviders(response.providers);
          const forcedProvider = response.forced_ai_provider;
          const forcedProviderAvailable = response.providers.some(
            (p: ProviderConfig) => p.id === forcedProvider
          );
          const selectedProviderAvailable = response.providers.some(
            (p: ProviderConfig) => p.id === value
          );

          if (response.providers.length > 0) {
            let nextProvider = response.providers[0].id;
            if (selectedProviderAvailable) {
              nextProvider = value;
            }
            if (forcedProvider && forcedProviderAvailable) {
              nextProvider = forcedProvider;
            }

            if (nextProvider !== value) {
              onChange(nextProvider);
            }
          }
        }
      } catch (err) {
        console.error("Failed to load providers:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return <div className="h-[44px] rounded-lg bg-[#f4f4f5] animate-pulse" />;
  }

  const selectedProvider = providers.find(p => p.id === value);
  const nonDevProviders = providers.filter(p => !p.dev_only);

  if (!expanded) {
    // Collapsed view
    return (
      <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[#faf5ff] border border-[#e9d5ff]">
        <CheckCircle2 size={18} className="text-[#8e2bf0] fill-[#8e2bf0] stroke-white shrink-0" />
        <span className="text-sm text-[#18181b]">
          {getProviderLabel(value)} – {selectedProvider?.supports_images
            ? t("documentUpload.selection.capabilityTextImages")
            : t("documentUpload.selection.capabilityTextOnly")}
          <span className="text-[#a1a1aa] ml-1">· {selectedProvider?.name}</span>
        </span>
        {nonDevProviders.length > 1 && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="ml-auto text-sm text-[#8e2bf0] hover:text-[#7c22d6] font-medium cursor-pointer"
          >
            {t("documentUpload.selection.change")}
          </button>
        )}
      </div>
    );
  }

  // Expanded view
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-[#18181b]">
          {t("documentUpload.selection.chooseMode")}
        </span>
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="text-sm text-[#8e2bf0] hover:text-[#7c22d6] font-medium cursor-pointer"
        >
          {t("documentUpload.selection.close")}
        </button>
      </div>
      {nonDevProviders.map((provider) => (
        <ProviderOption
          key={provider.id}
          provider={provider}
          selected={provider.id === value}
          label={getProviderLabel(provider.id)}
          description={getProviderDescription(provider.id)}
          onSelect={(id) => {
            onChange(id);
            setTimeout(() => setExpanded(false), 200);
          }}
        />
      ))}
    </div>
  );
}
